import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip,
  TextField,
} from '@mui/material';
import { DataGrid, GridColDef, GridActionsCellItem } from '@mui/x-data-grid';
import { arSD } from '@mui/x-data-grid/locales';
import { PersonAdd, Delete, AdminPanelSettings, CheckCircle, Cancel } from '@mui/icons-material';
import { getAdminUsers, createAdminUser, deleteAdminUser, type AdminUser } from '../lib/api';
import { FilterBar } from '../components/common';

const AdminUsersPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Form state
  const [newEmail, setNewEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    fetchAdminUsers();
  }, []);

  const fetchAdminUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const adminUsers = await getAdminUsers();
      setUsers(adminUsers);
    } catch (err: any) {
      console.error('Error fetching admin users:', err);
      setError(err.message || t('adminUsers.errorFetchingAdmins'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAdmin = async () => {
    setFormError(null);

    // Validate form
    if (!newEmail || !newPassword) {
      setFormError(t('adminUsers.emailRequired'));
      return;
    }

    if (newPassword.length < 6) {
      setFormError(t('adminUsers.passwordTooShort'));
      return;
    }

    try {
      await createAdminUser(newEmail, newPassword);
      setSuccess(t('adminUsers.adminCreatedSuccess'));
      setCreateDialogOpen(false);
      setNewEmail('');
      setNewPassword('');
      fetchAdminUsers();
    } catch (err: any) {
      console.error('Error creating admin user:', err);
      setFormError(err.message || t('adminUsers.errorCreatingAdmin'));
    }
  };

  const handleDeleteAdmin = async () => {
    if (!selectedUserId) return;

    try {
      await deleteAdminUser(selectedUserId);
      setSuccess(t('adminUsers.adminDeletedSuccess'));
      setDeleteDialogOpen(false);
      setSelectedUserId(null);
      fetchAdminUsers();
    } catch (err: any) {
      console.error('Error deleting admin user:', err);
      setError(err.message || t('adminUsers.errorDeletingAdmin'));
      setDeleteDialogOpen(false);
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'email',
      headerName: t('adminUsers.email'),
      flex: 1,
      minWidth: 250,
    },
    {
      field: 'user_metadata',
      headerName: t('adminUsers.fullName'),
      width: 200,
      valueGetter: (_value, row) => row.user_metadata?.full_name || '-',
    },
    {
      field: 'role',
      headerName: t('adminUsers.role'),
      width: 120,
      renderCell: () => (
        <Chip
          icon={<AdminPanelSettings />}
          label={t('adminUsers.admin')}
          color="primary"
          size="small"
        />
      ),
    },
    {
      field: 'email_confirmed_at',
      headerName: t('adminUsers.emailVerified'),
      width: 130,
      renderCell: (params) => (
        params.value ? (
          <Chip
            icon={<CheckCircle />}
            label={t('adminUsers.verified')}
            color="success"
            size="small"
            variant="outlined"
          />
        ) : (
          <Chip
            icon={<Cancel />}
            label={t('adminUsers.notVerified')}
            color="warning"
            size="small"
            variant="outlined"
          />
        )
      ),
    },
    {
      field: 'created_at',
      headerName: t('adminUsers.createdAt'),
      width: 180,
      valueFormatter: (value) => {
        if (!value) return '';
        return new Date(value).toLocaleString();
      },
    },
    {
      field: 'last_sign_in_at',
      headerName: t('adminUsers.lastSignIn'),
      width: 180,
      valueFormatter: (value) => {
        if (!value) return t('adminUsers.never');
        return new Date(value).toLocaleString();
      },
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: t('adminUsers.actions'),
      width: 100,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<Delete />}
          label={t('adminUsers.delete')}
          onClick={() => {
            setSelectedUserId(params.id as string);
            setDeleteDialogOpen(true);
          }}
        />,
      ],
    },
  ];

  const filteredUsers = users.filter((user) =>
    user.email.toLowerCase().includes(searchText.toLowerCase()) ||
    user.user_metadata?.full_name?.toLowerCase().includes(searchText.toLowerCase())
  );

  return (
    <Box>
      {/* Page Header with Stats */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography variant="h4" component="h1" sx={{ mb: 1 }}>
              {t('adminUsers.title')}
            </Typography>
            {/* Filter Stats */}
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <Chip
                label={`${t('adminUsers.total')}: ${users.length}`}
                color="primary"
                variant="outlined"
                size="small"
              />
              {filteredUsers.length !== users.length && (
                <Chip
                  label={`${t('adminUsers.filtered')}: ${filteredUsers.length}`}
                  color="success"
                  size="small"
                />
              )}
            </Box>
          </Box>
          <Button
            variant="contained"
            sx={{ gap: 1 }}
            onClick={() => setCreateDialogOpen(true)}
          >
            <PersonAdd sx={{ fontSize: 18 }} />{t('adminUsers.createAdmin')}
          </Button>
        </Box>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Filter Bar */}
      <FilterBar
        searchValue={searchText}
        onSearchChange={setSearchText}
        searchPlaceholder={t('adminUsers.searchPlaceholder')}
        filters={[]}
      />

      {/* Data Grid */}
      <Box sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={filteredUsers}
          columns={columns}
          loading={loading}
          pageSizeOptions={[10, 25, 50, 100]}
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } },
          }}
          disableRowSelectionOnClick
          localeText={isRTL ? arSD.components.MuiDataGrid.defaultProps.localeText : undefined}
          sx={{
            direction: isRTL ? 'rtl' : 'ltr',
          }}
        />
      </Box>

      {/* Create Admin Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => {
          setCreateDialogOpen(false);
          setNewEmail('');
          setNewPassword('');
          setFormError(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('adminUsers.createAdminUser')}</DialogTitle>
        <DialogContent>
          {formError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formError}
            </Alert>
          )}
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label={t('adminUsers.email')}
              type="email"
              fullWidth
              required
              size="small"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              autoFocus
            />
            <TextField
              label={t('adminUsers.password')}
              type="password"
              fullWidth
              required
              size="small"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              helperText={t('adminUsers.passwordHelper')}
            />
            <Alert severity="info">
              {t('adminUsers.createAdminInfo')}
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setCreateDialogOpen(false);
              setNewEmail('');
              setNewPassword('');
              setFormError(null);
            }}
          >
            {t('adminUsers.cancel')}
          </Button>
          <Button variant="contained" sx={{ gap: 1 }} onClick={handleCreateAdmin}>
            <PersonAdd sx={{ fontSize: 18 }} />{t('adminUsers.createAdmin')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setSelectedUserId(null);
        }}
      >
        <DialogTitle>{t('adminUsers.deleteAdminUser')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('adminUsers.deleteConfirmMessage')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteDialogOpen(false);
              setSelectedUserId(null);
            }}
          >
            {t('adminUsers.cancel')}
          </Button>
          <Button variant="contained" color="error" sx={{ gap: 1 }} onClick={handleDeleteAdmin}>
            <Delete sx={{ fontSize: 18 }} />{t('adminUsers.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminUsersPage;
